import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import type { Notification, NotificationsResponse } from '@/lib/schemas';
import { useSocketEvent } from '@/lib/socket';
import type { NotificationEvent } from '@/lib/socket';
import { useCallback } from 'react';

// ============================================
// QUERY KEYS
// ============================================

export const notificationKeys = {
    all: ['notifications'] as const,
    lists: () => [...notificationKeys.all, 'list'] as const,
    list: (filters?: { unread?: boolean }) =>
        [...notificationKeys.lists(), filters] as const,
    unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// ============================================
// HOOKS
// ============================================

export function useNotifications(options?: { unread?: boolean; limit?: number }) {
    return useQuery({
        queryKey: notificationKeys.list({ unread: options?.unread }),
        queryFn: async () => {
            const params = new URLSearchParams();
            if (options?.unread !== undefined) {
                params.set('unread', String(options.unread));
            }
            if (options?.limit) {
                params.set('limit', String(options.limit));
            }
            const queryString = params.toString();
            const endpoint = queryString ? `/notifications?${queryString}` : '/notifications';
            return apiGet<NotificationsResponse>(endpoint);
        },
        staleTime: 30 * 1000, // 30 seconds
    });
}

export function useInfiniteNotifications(options?: { unread?: boolean; limit?: number }) {
    const limit = options?.limit || 20;

    return useInfiniteQuery({
        queryKey: notificationKeys.list({ unread: options?.unread }),
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();
            if (options?.unread !== undefined) {
                params.set('unread', String(options.unread));
            }
            params.set('limit', String(limit));
            if (pageParam) {
                params.set('cursor', pageParam);
            }
            const endpoint = `/notifications?${params.toString()}`;
            return apiGet<NotificationsResponse>(endpoint);
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        initialPageParam: undefined as string | undefined,
        staleTime: 30 * 1000,
    });
}

export function useUnreadNotificationCount() {
    return useQuery({
        queryKey: notificationKeys.unreadCount(),
        queryFn: async () => {
            const data = await apiGet<{ unreadCount: number }>('/notifications/unread-count');
            return data.unreadCount;
        },
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000, // Refresh every minute
    });
}

export function useMarkNotificationRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (notificationId: string) => {
            return apiPost<{ id: string; isRead: boolean }>(
                `/notifications/${notificationId}/read`
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        },
    });
}

export function useMarkAllNotificationsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            return apiPost<{ markedAsRead: number }>('/notifications/read-all');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        },
    });
}

export function useDeleteNotification() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (notificationId: string) => {
            return apiDelete<{ message: string }>(`/notifications/${notificationId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        },
    });
}

export function useDeleteAllNotifications() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            return apiDelete<{ message: string; deleted: number }>('/notifications');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        },
    });
}

// ============================================
// REAL-TIME NOTIFICATION HOOK
// ============================================

export function useNotificationSubscription() {
    const queryClient = useQueryClient();

    const handleNewNotification = useCallback(
        (notification: NotificationEvent) => {
            // Invalidate queries to refetch
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });

            // Optionally, optimistically update the cache
            queryClient.setQueryData<NotificationsResponse>(
                notificationKeys.list({}),
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        notifications: [notification as Notification, ...old.notifications],
                        unreadCount: old.unreadCount + 1,
                    };
                }
            );
        },
        [queryClient]
    );

    // Subscribe to new notification events
    useSocketEvent<NotificationEvent>('notification:new', handleNewNotification);
}
